import { useEffect } from 'react';

import { Combobox, Datepicker, HookFormResolver, Number, Text, TextArea } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { File } from 'app/components/form';
import { factoryYup, limit } from 'app/core/factory-yup';
import { DateRequired, NumberRequired } from 'app/core/validations/common';
import { useGetApplication } from 'app/hooks';
import i18next from 'i18next';
import { Alert } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCatalogValueList } from '@/app/modules/configuration/catalogs/hooks/useCatalogValueList';
import {
  type CatalogModel,
  catalogName
} from '@/app/modules/configuration/catalogs/models/Catalog';

const yup = factoryYup();

const catalogIdInternational = '6b36a4ef-8bb4-42d3-98e3-03cec11f6927';
const validations = yup.object().shape({
  currency: yup.object().nullable().required(i18next.t('common.validations.fieldRequired')),
  paymentMethod: yup.object().nullable().required(i18next.t('common.validations.fieldRequired')),
  paymentDate: DateRequired,
  reference: yup.string().required(() => i18next.t('common.validations.fieldRequired')),
  exchangeRate: yup
    .number()
    .typeError(() => i18next.t('common.validations.fieldRequired'))
    .required(() => i18next.t('common.validations.fieldRequired')),
  amount: NumberRequired,
  swift: yup
    .string()
    .nullable()
    .max(limit.maxDescription)
    .when('paymentMethod', {
      is: (paymentMethod: any) => paymentMethod?.id === catalogIdInternational, // Si ES internacional
      then: (schema) => schema.required(i18next.t('common.validations.fieldRequired')),
      otherwise: (schema) => schema.notRequired()
    })
});

interface PaymentFormContentProps {
  nameForm: string;
  readOnly?: boolean;
  reject?: boolean;
  initialValues?: {
    paymentMethod: CatalogModel;
    currency: CatalogModel;
    amount: number;
    attachment: any;
    paymentDate?: Date;
    reference: string;
    swift?: string;
    state?: string;
    reason?: string;
    reject?: boolean;
  };
  onSubmit: (value: any) => void;
}

const Exchange = ({ currency, date }: { currency?: any; date?: Date }) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const [{ data }, execute] = useGetApplication<{ valueCurrency: number }>(
    {
      url: '/exchangerate',
      params: { currency: currency?.code, date }
    },
    false
  );

  const amount = watch('amount');
  const exchangeRate = watch('exchangeRate');

  useEffect(() => {
    if (currency && date) {
      execute();
    }
  }, [currency, date, execute]);

  useEffect(() => {
    if (data) {
      const nacionalAmount =
        (data?.valueCurrency ? data?.valueCurrency : 0) * (amount ? amount : 0);
      setValue('exchangeRate', data.valueCurrency);
      setValue('nacionalAmount', nacionalAmount);
    }
  }, [data, amount]);

  const labelExchangeRate = t('taxpayer-payments.label.exchange-rate');
  const labelLocalAmount = t('taxpayer-payments.label.local-amount');

  return (
    <Row className={(exchangeRate ?? 1) === 1 ? 'd-none' : ''}>
      <Col>
        <Number
          readonly={true}
          allowDecimal={true}
          includeThousandsSeparator={true}
          name="exchangeRate"
          label={labelExchangeRate}
        />
      </Col>
      <Col>
        <Number
          readonly={true}
          allowDecimal={true}
          includeThousandsSeparator={true}
          name="nacionalAmount"
          label={labelLocalAmount}
        />
      </Col>
    </Row>
  );
};

export const PaymentFormContent = ({
  nameForm,
  onSubmit,
  readOnly,
  initialValues
}: PaymentFormContentProps) => {
  const {
    isLoading: loading,
    error,
    data: currencies
  } = useCatalogValueList(catalogName.taxReturnCurrencies);

  const {
    isLoading: loadingPaymentMethod,
    error: errorPaymentMethod,
    data: paymentMethods
  } = useCatalogValueList(catalogName.paymentMethod);

  const isLoading = loading || loadingPaymentMethod || error || errorPaymentMethod;
  const { t } = useTranslation();

  if (isLoading) {
    return <></>;
  }

  const labelCurrency = t('common.label.currency');
  const labelPaymentMethod = t('common.label.paymentMethod');
  const labelAttachment = t('common.label.supportDocument');
  const labelReference = t('common.label.paymentReference');
  const labelAmount = t('taxpayer-payments.label.amount');
  const labelSwift = t('taxpayer-payments.label.swiftNumber');
  const labelReason = t('taxpayer-request-val.label.reason');

  return (
    <HookFormResolver
      initialValues={initialValues}
      validations={validations}
      nameForm={nameForm}
      onSubmit={onSubmit}
    >
      {({ watch, register }: HookFormMethods) => {
        const currency = watch('currency');
        const paymentDate = watch('paymentDate');
        const paymentMethod = watch('paymentMethod');
        return (
          <>
            <input
              defaultValue={initialValues?.reject ? 'S' : 'N'}
              {...register('reject')}
              hidden={true}
            />
            <Row>
              <Col>
                <Combobox
                  defaultValue={initialValues?.paymentMethod}
                  readOnly={readOnly}
                  code="id"
                  display="name"
                  data={paymentMethods}
                  label={labelPaymentMethod}
                  name="paymentMethod"
                />
              </Col>
              <Col>
                <Datepicker label={t('taxpayer-payments.label.date-payment')} name="paymentDate" />
              </Col>
            </Row>
            <Row>
              <Col>
                <Combobox
                  defaultValue={initialValues?.currency}
                  readOnly={readOnly}
                  code="id"
                  display="name"
                  data={currencies}
                  label={labelCurrency}
                  name="currency"
                />
              </Col>
              <Col>
                <Number
                  allowDecimal={true}
                  includeThousandsSeparator={true}
                  name="amount"
                  label={labelAmount}
                />{' '}
              </Col>
            </Row>
            <Exchange
              date={initialValues?.paymentDate ? initialValues.paymentDate : paymentDate}
              currency={initialValues?.currency ? initialValues.currency : currency}
            />
            <Row>
              <Col>
                <File
                  defaultValue={initialValues?.attachment}
                  label={labelAttachment}
                  accept="application/pdf"
                  name="attachment"
                  readonly={readOnly}
                />
              </Col>
            </Row>
            {paymentMethod?.id === catalogIdInternational && (
              <Row>
                <Col>
                  <Text readonly={readOnly} name="swift" label={labelSwift}></Text>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                {/* additional information */}
                <TextArea readOnly={readOnly} name="reference" label={labelReference} />
              </Col>
            </Row>

            {initialValues?.reason != undefined && initialValues?.reason !== '' && (
              <Row>
                <Col>
                  <TextArea
                    readOnly={initialValues?.state === 'rejected'}
                    name="reason"
                    label={labelReason}
                  />
                </Col>
              </Row>
            )}

            <Alert variant="warning">
              {t('taxpayer-payments.messages.paymentInformationWarning')}
            </Alert>
          </>
        );
      }}
    </HookFormResolver>
  );
};
